"use client";
import { useEffect, useRef, useCallback } from "react";
import { Mic, MicOff, Loader2, Volume2, Ear, EarOff } from "lucide-react";
import { useVoice } from "@/hooks/use-voice";
import { useWakeWord } from "@/hooks/use-wake-word";
import { useUIStore } from "@/stores/ui-store";
import { useGameStore } from "@/stores/game-store";

interface VoiceButtonProps {
  className?: string;
}

// Recording auto-stops this many ms after a wake-word triggered start,
// so the user can talk for a beat without holding any button.
const WAKE_RECORD_DURATION_MS = 5000;

export function VoiceButton({ className = "" }: VoiceButtonProps) {
  const { state, transcript, error, startRecording, stopRecording, speak } = useVoice();
  const setActivePanel = useUIStore((s) => s.setActivePanel);
  const setActiveSurggieId = useUIStore((s) => s.setActiveSurggieId);
  const surggies = useGameStore((s) => s.surggies);
  const lastTranscriptRef = useRef<string | null>(null);
  const wakeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleWake = useCallback(async (phrase: string) => {
    console.log(`[Wake] Heard "${phrase}" — starting recording`);
    await startRecording();
    if (wakeTimerRef.current) clearTimeout(wakeTimerRef.current);
    wakeTimerRef.current = setTimeout(() => {
      void stopRecording();
    }, WAKE_RECORD_DURATION_MS);
  }, [startRecording, stopRecording]);

  const wake = useWakeWord({ onDetect: handleWake });

  // When STT produces a transcript, route it to the Brain Surggie via /api/chat,
  // then speak the response.
  useEffect(() => {
    if (!transcript || transcript === lastTranscriptRef.current) return;
    lastTranscriptRef.current = transcript;

    const brain = surggies.find((s) => s.isPrimary) ?? surggies[0];
    if (!brain) return;

    setActiveSurggieId(brain.id);
    setActivePanel("chat");

    (async () => {
      try {
        const chatRes = await fetch("/api/chat", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ surggieId: brain.id, message: transcript }),
        });
        if (!chatRes.ok) return;
        const data = (await chatRes.json()) as { response?: string };
        if (data.response) {
          await speak(data.response);
        }
      } catch {
        // surfaced via error state in useVoice
      }
    })();
  }, [transcript, surggies, setActiveSurggieId, setActivePanel, speak]);

  const handleClick = async () => {
    if (state === "recording") {
      await stopRecording();
    } else if (state === "idle" || state === "error") {
      await startRecording();
    }
  };

  const buttonBase =
    "p-2 rounded transition-all duration-200 cursor-pointer";
  let icon = <Mic className="w-5 h-5" />;
  let stateClass = "text-[#5c4a30] hover:text-[#1a1a1a] hover:bg-[#b89968]/40";
  let title = "Push to talk";

  if (state === "recording") {
    icon = <MicOff className="w-5 h-5" />;
    stateClass = "text-[#c54a4a] bg-[#c54a4a]/15 border border-[#c54a4a] animate-pulse";
    title = "Recording — click to stop";
  } else if (state === "transcribing") {
    icon = <Loader2 className="w-5 h-5 animate-spin" />;
    stateClass = "text-[#c5a83a]";
    title = "Transcribing…";
  } else if (state === "speaking") {
    icon = <Volume2 className="w-5 h-5" />;
    stateClass = "text-[#5c7935] bg-[#5c7935]/15 border border-[#5c7935]";
    title = "Brain is speaking…";
  } else if (state === "error") {
    stateClass = "text-[#c54a4a] hover:text-[#a03a3a]";
    title = error ? `Error: ${error} — click to retry` : "Error";
  }

  // Wake-word toggle styling
  const wakeBase = "p-2 rounded transition-colors duration-150 cursor-pointer";
  const wakeActive =
    "text-[#a04ab8] bg-[#a04ab8]/15 border border-[#a04ab8]";
  const wakeInactive =
    "text-[#8a7a5c] hover:text-[#a04ab8] hover:bg-[#b89968]/40";
  const wakeDisabled = "text-[#b89968] cursor-not-allowed";

  return (
    <div className={`flex items-center gap-1 ${className}`}>
      <button
        onClick={handleClick}
        className={`${buttonBase} ${stateClass}`}
        title={title}
        disabled={state === "transcribing" || state === "speaking"}
      >
        {icon}
      </button>
      <button
        onClick={() => wake.toggle()}
        className={`${wakeBase} ${
          !wake.supported ? wakeDisabled : wake.enabled ? wakeActive : wakeInactive
        }`}
        title={
          !wake.supported
            ? "Wake-word not supported in this browser"
            : wake.enabled
            ? `Wake-word ON (say "hey brain")${wake.lastHeard ? ` — heard: "${wake.lastHeard.slice(0, 40)}"` : ""}`
            : 'Enable wake-word ("hey brain")'
        }
        disabled={!wake.supported}
        suppressHydrationWarning
      >
        {wake.enabled ? <Ear className="w-5 h-5" /> : <EarOff className="w-5 h-5" />}
      </button>
    </div>
  );
}
